/**
 * @module SliderComponent
 * @description Carousel/slider with CSS snap scrolling, touch/mouse drag,
 * keyboard navigation, autoplay, and pagination dot controls.
 * @requires @fc/component
 * @requires @fc/utilities
 * @requires @fc/scrolling
 * @requires @fc/events
 * @customElement slide-carousel
 */
import { FCElement } from '@fc/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@fc/utilities';
import { Scroller, scrollIntoView } from '@fc/scrolling';
import { CarouselPickEvent } from '@fc/events';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const MOUSE_DRAG_THRESHOLD = 6;
const INTERACTIVE_DRAG_EXCLUSION_SELECTOR =
  'a[href], area[href], button, input, label, select, textarea, summary, [role="button"], [role="checkbox"], [role="menuitem"], [role="option"], [role="radio"], [role="switch"], [contenteditable="true"]';

export class Carousel extends FCElement {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  requiredRefs = ['scroller'];

  #ac = null;
  #off = false;
  #timer = undefined;
  #scrl;
  #ro;
  #idx = 0;
  #drag = false;
  #prevScroll = 0;
  #loopTimer = undefined;
  #looping = false;
  #wheelT = 0;
  #persistKey = null;
  #visibleObserver;
  #visibleObserverReady = false;
  #visibleSlides = [];
  #suppressBoundaryLoop = false;
  #suppressBoundaryLoopTimer = undefined;

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'initial-slide' || oldValue === newValue) return;

    queueMicrotask(() => {
      if (!this.isConnected || !this.#scrl || !this.refs.slides) return;

      let idx = parseInt(newValue, 10);
      if (!isFinite(idx) || idx < 0) idx = 0;

      const { slides } = this.refs;
      if (!Array.isArray(slides) || idx >= slides.length) return;

      const slide = slides[idx];
      if (!(slide instanceof HTMLElement)) return;

      const sid = slide.getAttribute('slide-id');
      if (sid && typeof sid === 'string') {
        this.select({ id: sid }, undefined, { animate: false });
      }
    });
  }

  async connectedCallback() {
    super.connectedCallback();

    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    if (viewTransition?.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#scrl = new Scroller(scroller, {
      onScroll: this.#onScroll,
      onScrollStart: this.#onScrollStart,
      onScrollEnd: this.#onScrollEnd,
    });

    scroller.addEventListener('mousedown', this.#onMouseDown, { signal });
    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    this.addEventListener('focusin', this.suspend, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('pointerenter', this.#onPointerIn, { signal });
    document.addEventListener('visibilitychange', this.#onVisChange, { signal });

    scroller.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    scroller.addEventListener('keydown', this.#onKeyDown, { signal });
    scroller.addEventListener('focus', this.#onScrollerFocus, { signal });

    this.#setupVisibleObserver();
    this.#refreshControls();
    this.disabled = this.isNested || this.disabled;
    this.resume();
    this.current = this.initialSlideIndex;

    scheduler?.schedule?.(() => {
      let visCount = 0;
      const initSlide = this.initialSlide;
      const initId = initSlide instanceof HTMLElement ? initSlide.getAttribute('slide-id') : null;

      if (this.initialSlideIndex !== 0 && initId) {
        this.select({ id: initId }, undefined, { animate: false });
        visCount = 1;
      } else {
        visCount = this.#refreshVisible();
        if (visCount === 0) {
          this.select(0, undefined, { animate: false });
          visCount = 1;
        }
      }
      this.#syncScrollerFocusPolicy();

      const container = this.refs.carouselContainer;
      if (container instanceof HTMLElement) {
        this.#ro = new ResizeObserver(async () => {
          if (viewTransition?.current) await viewTransition.current;
          visCount = this.#refreshVisible();
          this.#syncControlDisabledState();
          if (this.hasAttribute('auto-hide-controls')) this.#refreshControls();
          this.#syncScrollerFocusPolicy();
        });
        this.#ro.observe(container);
      }

      if (this.hasAttribute('persist-scroll')) {
        const key = this.getAttribute('persist-scroll');
        const el = this.refs.scroller;
        this.#persistKey = key;

        const restorePos = (px) => {
          if (px > 0 && el) {
            const max = el.scrollWidth - el.clientWidth;
            this.#scrl.snap = false;
            el.scrollLeft = Math.min(px, max);
            requestAnimationFrame(() => {
              this.#scrl.snap = true;
              this.#recalc();
            });
          }
        };

        restorePos(parseFloat(sessionStorage.getItem(key)) || 0);

        window.addEventListener(
          'pageshow',
          (ev) => {
            if (!ev.persisted) return;
            restorePos(parseFloat(sessionStorage.getItem(key)) || 0);
          },
          { signal },
        );
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
    this.#ac = null;
    this.suspend();
    if (this.#loopTimer) {
      clearTimeout(this.#loopTimer);
      this.#loopTimer = undefined;
    }
    if (this.#suppressBoundaryLoopTimer) {
      clearTimeout(this.#suppressBoundaryLoopTimer);
      this.#suppressBoundaryLoopTimer = undefined;
    }
    this.#scrl?.destroy?.();
    this.#ro?.disconnect();
    this.#ro = undefined;
    this.#visibleObserver?.disconnect();
    this.#visibleObserver = undefined;
    this.#visibleSlides = [];
    this.#visibleObserverReady = false;
  }

  get isNested() {
    return this.parentElement?.closest('slide-carousel') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  get initialSlideIndex() {
    const val = this.getAttribute('initial-slide');
    // eslint-disable-next-line eqeqeq
    return val == null ? 0 : parseInt(val, 10);
  }

  get slides() {
    return this.refs.slides?.filter((s) => !s.hasAttribute('hidden') || s.hasAttribute('reveal'));
  }

  get current() {
    return this.#idx;
  }

  set current(value) {
    const { slides } = this.refs;
    this.#idx = value;

    for (const controls of this.#controlGroups) {
      for (const counter of controls.querySelectorAll('[data-ref="current"]')) {
        counter.textContent = `${value + 1}`;
      }

      for (const ref of ['thumbnails[]', 'dots[]']) {
        const group = controls.querySelectorAll(`[data-ref="${ref}"]`);
        group.forEach((element, index) => element.setAttribute('aria-current', `${index === value}`));
      }

      for (const progressFill of controls.querySelectorAll('[data-ref="progressFill"]')) {
        if (!(progressFill instanceof HTMLElement) || !slides?.length) continue;

        const pct = ((value + 1) / slides.length) * 100;
        progressFill.style.width = `${pct}%`;
        const bar = progressFill.closest('[role="progressbar"]');
        if (bar) bar.setAttribute('aria-valuenow', `${value + 1}`);
      }
    }

    this.#syncControlDisabledState(value);
  }

  get infinite() {
    // eslint-disable-next-line eqeqeq
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    const { slides } = this;
    if (this.#visibleObserverReady && this.#visibleSlides.length && slides?.length) {
      return slides.filter((slide) => this.#visibleSlides.includes(slide));
    }

    return getVisibleElements(this.refs.scroller, slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const step = parseInt(this.getAttribute('step'), 10);
    const mod = step || Math.max(this.visibleSlides.length, 1);
    return this.current - mod;
  }

  get nextIndex() {
    const step = parseInt(this.getAttribute('step'), 10);
    const mod = step || Math.max(this.visibleSlides.length, 1);
    return this.current + mod;
  }

  get atStart() {
    return this.slides?.length ? this.current === 0 : false;
  }

  get atEnd() {
    const { slides } = this;
    return slides?.length ? this.current === slides.length - 1 : false;
  }

  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    value ? this.setAttribute('paused', '') : this.removeAttribute('paused');
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const v = parseInt(`${this.getAttribute('autoplay')}`, 10);
    return Number.isNaN(v) ? undefined : v * 1000;
  }

  async select(input, event, options = {}) {
    if (this.#off || !this.refs.slides?.length) return;

    for (const s of this.refs.slides) {
      if (s.hasAttribute('reveal')) {
        s.removeAttribute('reveal');
        s.setAttribute('aria-hidden', 'true');
        s.setAttribute('inert', '');
      }
    }

    let reqIdx =
      typeof input === 'number'
        ? input
        : typeof input === 'string'
          ? parseInt(input, 10)
          : (() => {
              const target = this.refs.slides.find((s) => s.getAttribute('slide-id') === input.id);
              if (!target || !this.slides) return undefined;
              if (target.hasAttribute('hidden')) {
                target.setAttribute('reveal', '');
                target.setAttribute('aria-hidden', 'false');
                target.removeAttribute('inert');
                this.#visibleObserver?.observe(target);
              }
              return this.slides.indexOf(target);
            })();

    const { current } = this;
    if (reqIdx === undefined || isNaN(reqIdx) || reqIdx === current) return;

    const { slides } = this;
    if (!slides?.length) return;

    if (!this.infinite) reqIdx = clamp(reqIdx, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const last = slides.length - 1;

    const idx = reqIdx < 0 ? last : reqIdx > last ? 0 : reqIdx;

    const adjacent = Math.abs(idx - current) <= 1 && reqIdx >= 0 && reqIdx <= last;
    const { visibleSlides } = this;
    // Non-adjacent jumps (e.g. thumbnail clicks) use instant scroll to avoid
    // race conditions between DOM reorder, scroll-snap, and scroll anchoring.
    const instant = prefersReducedMotion() || !animate || (!adjacent && visibleSlides.length === 1);

    const slide = slides[idx];
    if (!slide) return;

    const prevIdx = this.current;

    slide.setAttribute('aria-hidden', 'false');
    slide.removeAttribute('inert');
    this.#suppressNextBoundaryLoopCheck();
    this.#scrl.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    this.#scrollThumb(idx, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new CarouselPickEvent({
        index: idx,
        previousIndex: prevIdx,
        // eslint-disable-next-line eqeqeq
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  }

  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  play(interval = this.autoplayInterval) {
    if (this.#timer) return;
    this.paused = false;
    this.#timer = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  suspend() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  refresh() {
    this.onMorphSync?.();

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#setupVisibleObserver();
    this.#recalc();
    this.#refreshVisible();
    this.#syncControlDisabledState();
    if (this.hasAttribute('auto-hide-controls')) this.#refreshControls();
    this.#syncScrollerFocusPolicy();
  }

  // Scroll handler — syncs index, persists position, handles boundary looping
  #onScroll = () => {
    if (this.#persistKey) {
      try {
        sessionStorage.setItem(this.#persistKey, String(this.refs.scroller.scrollLeft));
      } catch {}
    }

    const prev = this.#idx;
    const idx = this.#recalc();
    this.#syncControlDisabledState();

    /* #prevScroll is captured at scroll-start for infinite-loop detection */

    if (idx === prev) return;

    const slide = this.slides?.[idx];
    if (!slide) return;

    this.dispatchEvent(
      new CarouselPickEvent({
        index: idx,
        previousIndex: prev,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  };

  #onScrollStart = () => {
    this.setAttribute('transitioning', '');
    // Capture scroll position at start for infinite-loop boundary detection
    this.#prevScroll = this.refs.scroller?.scrollLeft ?? 0;
  };

  #onScrollEnd = () => {
    const suppressBoundaryLoop = this.#consumeBoundaryLoopSuppression();
    this.#refreshVisible();
    this.#syncControlDisabledState();
    this.#syncScrollerFocusPolicy();
    this.removeAttribute('transitioning');
    if (suppressBoundaryLoop) return;
    this.#checkInfiniteLoop();
  };

  // Infinite-loop boundary detection — runs after scroll has fully settled.
  // If the user scrolled to the first or last slide boundary, wrap around.
  #checkInfiniteLoop() {
    if (!this.infinite || this.#scrl.axis !== 'x' || this.#looping) return;
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;
    const { slides } = this;
    if (!slides?.length || slides.length <= 1) return;

    const sl = scroller.scrollLeft;
    const max = scroller.scrollWidth - scroller.clientWidth;
    const atLeft = sl <= 5 && this.#idx === 0;
    const atRight = sl >= max - 5 && this.#idx === slides.length - 1;

    // Only loop if the user was already at this boundary before the scroll —
    // i.e. they tried to scroll past the edge but ended up in the same place.
    const wasAtSameEdge =
      (atLeft && this.#prevScroll <= 5) || (atRight && this.#prevScroll >= max - 5);
    if (!wasAtSameEdge) return;

    if (atLeft) {
      this.#looping = true;
      this.#scrl.to(slides[slides.length - 1], { instant: false });
      this.current = slides.length - 1;
      setTimeout(() => { this.#looping = false; }, 300);
    } else if (atRight) {
      this.#looping = true;
      this.#scrl.to(slides[0], { instant: false });
      this.current = 0;
      setTimeout(() => { this.#looping = false; }, 300);
    }
  }

  #suppressNextBoundaryLoopCheck() {
    this.#suppressBoundaryLoop = true;
    if (this.#suppressBoundaryLoopTimer) clearTimeout(this.#suppressBoundaryLoopTimer);

    this.#suppressBoundaryLoopTimer = setTimeout(() => {
      this.#suppressBoundaryLoop = false;
      this.#suppressBoundaryLoopTimer = undefined;
    }, 800);
  }

  #consumeBoundaryLoopSuppression() {
    if (!this.#suppressBoundaryLoop) return false;

    this.#suppressBoundaryLoop = false;
    if (this.#suppressBoundaryLoopTimer) {
      clearTimeout(this.#suppressBoundaryLoopTimer);
      this.#suppressBoundaryLoopTimer = undefined;
    }

    return true;
  }

  // Recalculate which slide is closest to viewport center
  #recalc = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const vis = this.visibleSlides;
    if (!vis.length) return this.current;

    const { axis } = this.#scrl;
    const { scroller } = this.refs;
    const centers = vis.map((s) => center(s, axis));
    const ref = vis.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const near = closest(centers, ref);
    const match = vis[centers.indexOf(near)];

    return (this.current = match ? slides.indexOf(match) : 0);
  };

  #onMouseDown = (event) => {
    const { slides } = this;
    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (event.button !== 0) return;
    if (this.disabled || this.#drag) return;
    if (event.target.closest('model-viewer')) return;
    if (event.target.closest(INTERACTIVE_DRAG_EXCLUSION_SELECTOR)) return;

    const { axis } = this.#scrl;
    const startPos = event[axis];
    const ac = new AbortController();
    const { signal } = ac;
    const t0 = performance.now();
    let prev = startPos;
    let vel = 0;
    let moved = false;
    let dist = 0;

    this.#drag = true;

    const onMove = (ev) => {
      const cur = ev[axis];
      const initDelta = startPos - cur;
      if (!initDelta) return;
      if (!moved && Math.abs(initDelta) < MOUSE_DRAG_THRESHOLD) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(ev.pointerId);
        document.addEventListener('click', preventDefault, { once: true, signal });

        const goRight = initDelta < 0;
        const goLeft = initDelta > 0;
        const parent = this.parentElement?.closest('slide-carousel');
        const nested = parent instanceof Carousel && parent !== this;
        const blocked = (goRight && this.atStart) || (goLeft && this.atEnd);

        if (nested && blocked) {
          ac.abort();
          this.#drag = false;
          this.#scrl.snap = true;
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      ev.preventDefault();
      ev.stopImmediatePropagation();

      const delta = prev - cur;
      vel = Math.round((delta / (performance.now() - t0)) * 1000);
      prev = cur;
      dist += Math.abs(delta);
      this.#scrl.by(delta, { instant: true });
    };

    const onUp = async (ev) => {
      ac.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;
      this.#drag = false;

      if (!slides?.length || !scroller) return;
      if (!moved) {
        this.#scrl.snap = true;
        this.resume();
        return;
      }

      const dir = Math.sign(vel);
      const synced = this.#recalc();
      const mod = current !== synced || Math.abs(vel) < 10 || dist < 10 ? 0 : dir;
      const newIdx = clamp(synced + mod, 0, slides.length - 1);
      const newSlide = slides[newIdx];
      const prevIdx = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIdx}`);

      this.#scrl.to(newSlide);
      this.removeAttribute('dragging');
      if (this.hasPointerCapture(ev.pointerId)) this.releasePointerCapture(ev.pointerId);
      this.#scrollThumb(newIdx);

      this.dispatchEvent(
        new CarouselPickEvent({
          index: newIdx,
          previousIndex: prevIdx,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        }),
      );

      this.current = newIdx;
      await this.#scrl.finished;

      if (this.#drag) return;
      this.#scrl.snap = true;
      this.resume();
    };

    this.#scrl.snap = false;
    document.addEventListener('pointermove', onMove, { signal });
    document.addEventListener('pointerup', onUp, { signal });
    // Fix: first tap-drag on zoom dialog captured unexpectedly
    document.addEventListener('pointerdown', onUp, { signal });
    document.addEventListener('pointercancel', onUp, { signal });
    document.addEventListener('pointercapturelost', onUp, { signal });
  };

  #onPointerIn = () => {
    this.setAttribute('actioned', '');
  };

  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.resume();
  };

  #onScrollerFocus = (event) => {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement) || event.target !== scroller) return;

    const rect = scroller.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    if (rect.top >= 0 && rect.bottom <= viewportHeight) return;

    scroller.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  };

  #onKeyDown = (event) => {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement) || event.target !== scroller) return;
    if (!this.slides?.length) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previous(event, { animate: true });
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next(event, { animate: true });
        break;
      case 'Home':
        event.preventDefault();
        this.select(0, event, { animate: true });
        break;
      case 'End':
        event.preventDefault();
        this.select(this.slides.length - 1, event, { animate: true });
        break;
      default:
        break;
    }
  };

  #onVisChange = () => (document.hidden ? this.pause() : this.resume());

  #onWheel = (event) => {
    if (this.#scrl.axis !== 'x') return;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return;

    const { slides } = this;
    if (!slides || slides.length <= 1) return;

    const sl = scroller.scrollLeft;
    const max = scroller.scrollWidth - scroller.clientWidth;
    const atLeft = sl <= 5;
    const atRight = sl >= max - 5;
    const dx = Math.abs(event.deltaX);
    const pastStart = atLeft && event.deltaX > 0 && dx > 5;
    const pastEnd = atRight && event.deltaX < 0 && dx > 5;

    if (!pastStart && !pastEnd) return;

    event.preventDefault();

    if (!this.infinite || this.#looping) return;

    if (pastStart) {
      this.#looping = true;
      this.#scrl.to(slides[slides.length - 1], { instant: false });
      this.current = slides.length - 1;
      setTimeout(() => {
        this.#looping = false;
      }, 300);
      return;
    }

    if (pastEnd) {
      this.#looping = true;
      this.#scrl.to(slides[0], { instant: false });
      this.current = 0;
      setTimeout(() => {
        this.#looping = false;
      }, 300);
    }
  };

  #refreshControls() {
    if (!this.hasAttribute('auto-hide-controls')) return;
    const { scroller, carouselControls } = this.refs;
    if (!(carouselControls instanceof HTMLElement)) return;
    carouselControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  #setupVisibleObserver() {
    this.#visibleObserver?.disconnect();
    this.#visibleObserver = undefined;
    this.#visibleObserverReady = false;
    this.#visibleSlides = [];

    const { scroller } = this.refs;
    const { slides } = this;
    if (!(scroller instanceof HTMLElement) || !slides?.length || typeof IntersectionObserver === 'undefined') return;

    this.#visibleObserver = new IntersectionObserver(
      (entries) => {
        const currentSlides = this.slides || [];
        if (!currentSlides.length) return;

        const visibleSlides = new Set(this.#visibleSlides.filter((slide) => currentSlides.includes(slide)));

        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement) || !currentSlides.includes(entry.target)) continue;

          if (entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD) {
            visibleSlides.add(entry.target);
          } else {
            visibleSlides.delete(entry.target);
          }
        }

        this.#visibleSlides = currentSlides.filter((slide) => visibleSlides.has(slide));
        this.#visibleObserverReady = true;

        scheduler.schedule(() => {
          this.#refreshVisible();
          this.#syncControlDisabledState();
          this.#syncScrollerFocusPolicy();
        });
      },
      {
        root: scroller,
        rootMargin: '1px',
        threshold: SLIDE_VISIBLITY_THRESHOLD,
      },
    );

    for (const slide of slides) {
      this.#visibleObserver.observe(slide);
    }
  }

  #syncControlDisabledState(index = this.current) {
    const { previous, next, scroller } = this.refs;
    const canScroll = scroller instanceof HTMLElement ? scroller.scrollWidth > scroller.clientWidth + 1 : true;

    if (!canScroll || !(scroller instanceof HTMLElement)) {
      this.#setControlDisabled(previous, true);
      this.#setControlDisabled(next, true);
      return;
    }

    if (this.infinite) {
      this.#setControlDisabled(previous, false);
      this.#setControlDisabled(next, false);
      return;
    }

    const fit_max_scroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const fit_scroll_left = Math.max(0, Math.min(scroller.scrollLeft, fit_max_scroll));
    const fit_current_index = Number.isFinite(Number(index)) ? Number(index) : 0;
    const fit_slide_count = this.slides?.length ?? 0;
    const fit_index_at_start = fit_current_index <= 0;
    const fit_index_at_end = fit_slide_count > 0 ? fit_current_index >= fit_slide_count - 1 : true;
    const fit_at_start = fit_scroll_left <= 1 && fit_index_at_start;
    const fit_at_end = fit_scroll_left >= fit_max_scroll - 1 || fit_index_at_end;

    this.#setControlDisabled(previous, fit_at_start);
    this.#setControlDisabled(next, fit_at_end);
  }

  #setControlDisabled(control, disabled) {
    if (!(control instanceof HTMLButtonElement)) return;

    control.disabled = disabled;
    control.setAttribute('aria-disabled', String(disabled));
  }

  #syncScrollerFocusPolicy() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    const scrollable =
      scroller.scrollWidth > scroller.clientWidth + 1 || scroller.scrollHeight > scroller.clientHeight + 1;
    if (scrollable) {
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('data-fit-carousel-scroller-focus', 'fallback');
      return;
    }

    scroller.removeAttribute('data-fit-carousel-scroller-focus');
  }

  #scrollThumb(idx, behavior = 'smooth') {
    for (const controls of this.#controlGroups) {
      const thumbnailsContainer = controls.querySelector('[data-ref="thumbnailsContainer"]');
      const thumbnails = controls.querySelectorAll('[data-ref="thumbnails[]"]');
      const thumb = thumbnails[idx];
      if (!(thumb instanceof HTMLElement) || !(thumbnailsContainer instanceof HTMLElement)) continue;

      scrollIntoView(thumb, {
        ancestor: thumbnailsContainer,
        behavior,
        block: 'center',
        inline: 'center',
      });
    }
  }

  get #controlGroups() {
    return Array.from(this.querySelectorAll('carousel-controls')).filter(
      (controls) => controls.closest('slide-carousel') === this,
    );
  }

  #refreshVisible() {
    const { slides } = this;
    if (!slides?.length) return 0;

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return 0;

    const rect = scroller.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;

    const vis = this.visibleSlides;
    if (!vis.length) return 0;

    scheduler.schedule(() => {
      for (const s of slides) {
        const isHidden = !vis.includes(s);
        s.setAttribute('aria-hidden', `${isHidden}`);
        if (isHidden) {
          s.setAttribute('inert', '');
        } else {
          s.removeAttribute('inert');
        }
      }
      this.#syncScrollerFocusPolicy();
    });
    return vis.length;
  }
}

if (!customElements.get('slide-carousel')) {
  customElements.define('slide-carousel', Carousel);
}
